import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { siteInfo, faqData, testimonials } from "@/mocks/data";
import JsonLd from "@/components/base/JsonLd";

export default function AboutPage() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://showpath4.github.io/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "关于我们",
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <Layout>
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={faqSchema} />
      <section className="w-full pt-24 pb-6 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 font-medium">关于我们</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
            关于我们
          </h1>
          <p className="text-sm text-foreground-600">了解{siteInfo.name}的故事与使命</p>
        </div>
      </section>

      <section className="w-full py-14 md:py-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                品牌介绍
              </h2>
              <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                <p>
                  <strong className="text-foreground-900">妈妈的朋友2 影迷资讯站</strong>成立于{siteInfo.foundingYear}年，
                  是一个专注于为影迷提供高质量影视资讯的中文平台。我们的使命是帮助每一位对电影《妈妈的朋友2》感兴趣的观众，
                  快速找到最全面、最准确的内容解析和观看指南。
                </p>
                <p>
                  我们深知，在信息爆炸的时代，影迷们需要的不仅仅是零散的资讯，更是经过精心整理和深度分析的内容。
                  因此，我们的每一篇文章都由资深的影视内容团队精心撰写，力求为读者提供最有价值的阅读体验。
                </p>
                <p>
                  从剧情解析到演员阵容，从观看指南到专业影评，我们覆盖了影迷关心的每一个维度。
                  无论你是第一次接触这部电影，还是已经对其了如指掌，这里都能为你带来新的收获和启发。
                </p>
                <p>
                  未来，我们将继续拓展内容领域，为影迷们带来更多优质的影视资讯。感谢每一位读者的支持和信任！
                </p>
              </div>
            </div>

            <div className="relative w-full h-72 md:h-80 rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20library%20or%20reading%20room%20with%20soft%20golden%20lighting%2C%20bookshelves%20and%20comfortable%20chairs%2C%20elegant%20literary%20atmosphere%2C%20film%20culture%20aesthetic%2C%20warm%20amber%20and%20cognac%20tones%2C%20cozy%20intellectual%20space%2C%20vintage%20charm%2C%20high%20quality%20photography&width=900&height=700&seq=about-brand&orientation=landscape"
                alt="关于我们 - 品牌形象"
                className="w-full h-full object-cover object-top"
                title="妈妈的朋友2 影迷资讯站品牌形象"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-12 text-center">
            数据一览
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className="ri-article-line text-xl"></i>
              </div>
              <div className="font-heading text-3xl font-bold text-foreground-900 mb-1">
                {siteInfo.totalArticles}+
              </div>
              <p className="text-xs text-foreground-600">原创文章</p>
            </div>
            <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                <i className="ri-eye-line text-xl"></i>
              </div>
              <div className="font-heading text-3xl font-bold text-foreground-900 mb-1">
                {siteInfo.totalViews >= 10000 ? `${(siteInfo.totalViews / 10000).toFixed(1)}万` : siteInfo.totalViews}+
              </div>
              <p className="text-xs text-foreground-600">累计阅读量</p>
            </div>
            <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600">
                <i className="ri-star-fill text-xl"></i>
              </div>
              <div className="font-heading text-3xl font-bold text-foreground-900 mb-1">
                {siteInfo.avgRating}
              </div>
              <p className="text-xs text-foreground-600">平均评分</p>
            </div>
            <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className="ri-user-smile-line text-xl"></i>
              </div>
              <div className="font-heading text-3xl font-bold text-foreground-900 mb-1">
                {testimonials.length}+
              </div>
              <p className="text-xs text-foreground-600">好评用户</p>
            </div>
          </div>
        </div>
      </section>

      <section id="testimonials" className="w-full py-14 md:py-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-12 text-center">
            用户评价
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="p-6 rounded-xl bg-background-50 border border-background-200/70"
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-sm ${i < t.rating ? "ri-star-fill text-accent-500" : "ri-star-fill text-background-300"}`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  &ldquo;{t.comment}&rdquo;
                </p>
                <div className="flex items-center justify-between text-xs text-foreground-500">
                  <span className="font-medium text-foreground-800">{t.name}</span>
                  <span>{t.location} · {t.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-12 text-center">
            常见问题
          </h2>
          <div className="space-y-3">
            {faqData.map((faq, idx) => (
              <details
                key={idx}
                className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
              >
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                  <span className="text-sm font-medium text-foreground-800 pr-4">
                    {faq.question}
                  </span>
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-open:bg-primary-100 group-open:text-primary-600 transition-colors">
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform duration-200"></i>
                  </span>
                </summary>
                <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="w-full py-14 md:py-20 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4 text-center">
            联系我们
          </h2>
          <p className="text-sm text-foreground-600 text-center mb-10">
            如果你有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className="ri-mail-line text-xl"></i>
              </div>
              <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
              <p className="text-xs text-foreground-600">contact@movieinfo.example.com</p>
            </div>
            <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                <i className="ri-wechat-line text-xl"></i>
              </div>
              <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">微信公众号</h3>
              <p className="text-xs text-foreground-600">妈妈的朋友2影迷站</p>
            </div>
            <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600">
                <i className="ri-timer-line text-xl"></i>
              </div>
              <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
              <p className="text-xs text-foreground-600">周一至周五 9:00-18:00</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}